/**
 * Legend hosting for the sheet element: rebuild the legend columns for the
 * bound sheet, measure their width and apply it (`rebindLegend`), with the
 * measure-debug comparison. Moved out of sheet-element.ts (r5 FS 2); the
 * element hands in its legends through `LegendHostingHost`. This module owns
 * `LegendHostingState`: its measurer caches and the drag-resize state.
 */
import { LegendSentinel } from "./legend-sentinel.ts";
import { type Option } from "@tento-lona/sheets";
import { RowLocalId } from "@tento-lona/sheets";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { $$ } from "@tento-ui/fastdom";
import { RenderPluginManager } from "./render-plugin.ts";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import { PretextLegendMeasurer } from "./legend-pretext-measurer.ts";
import { SheetLegend } from "./sheet-legend.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";
import type { SheetElementCore } from "./sheet-element-core.ts";

/** A pretext width measurement against the DOM sentinel's (the legend measure-debug toast). */
export type LegendMeasureComparison = {
  pretextWidth: number;
  pretextMs: number;
  sentinelWidth: number;
  sentinelMs: number;
  diff: number;
  /** The row whose pretext width was widest. */
  widestRow: Option<string>;
};

/** Legend hosting's caches and drag-resize state. Owner: this module. */
export class LegendHostingState {
  /** The cached pretext measurer (made on first use). */
  pretextMeasurer: Option<PretextLegendMeasurer> = null;
  /** The DOM sentinel for the measure-debug comparison (made on first use). */
  legendSentinel: Option<LegendSentinel> = null;
  /** Whether a legend drag-resize is in progress. */
  isDragResizing = false;
  /** The drag-resize watchdog timer. */
  dragResizeTimeout: Option<number> = null;
  /** The body margin-top the last drag-resize applied. */
  lastDragBodyMarginTop: Option<number> = null;
}

/**
 * During a drag-resize's height-only render: record `bodyMarginTop` as the
 * one applied, and answer whether it changed (the caller applies it then).
 */
export function dragBodyMarginTopChanged(state: LegendHostingState, bodyMarginTop: number): boolean {
  if (state.lastDragBodyMarginTop === bodyMarginTop) return false;
  state.lastDragBodyMarginTop = bodyMarginTop;
  return true;
}

/** What legend hosting reads on its sheet element (read when a step runs), and the state it writes. */
export interface LegendHostingHost {
  /** The element as every step reads it (its attributes, shadow root and style context; the bound sheet). */
  readonly core: SheetElementCore;
  /** The legend state; written here only. */
  readonly state: LegendHostingState;
  /** The element's delegate handle. */
  readonly handle: SheetDelegate;
  /** The body, header and footer legends. */
  readonly legends: { readonly legend: SheetLegend; readonly headerLegend: SheetLegend; readonly footerLegend: SheetLegend };
  /** The legend column (`#legend`). */
  readonly legendColumn: HTMLElement;
  /** The row whose options are open. */
  readonly activeRowOptions: LiveData<Option<RowLocalId>>;
  /** The legend's row-resize delegate. */
  readonly resizeDelegate: SheetLegend.ResizeDelegate;
  /** The installed plugin manager, if any. */
  readonly pluginManager: Option<RenderPluginManager>;
  /** Forget the emit's visible-columns key: a rebuilt legend must repaint. */
  forgetVisibleColumns(): void;
  /** Whether the legend rail is collapsed. */
  readonly legendCollapsed: boolean;
  /** The caller's legend width, taken verbatim. */
  readonly legendWidthOverridePx: Option<number>;
  /** The collapsed rail's width, if the caller set one. */
  readonly legendCollapsedWidthPx: Option<number>;
  /** `SheetElement.DEFAULT_COLLAPSED_LEGEND_PX`. */
  readonly defaultCollapsedLegendPx: number;
  /** The width the legend occludes on the left. */
  readonly leftOccludedWidthPx: LiveData<number>;
  /** Debug: show the measurement toast. */
  readonly legendMeasureToast: Option<LiveData.Readable<boolean>>;
  /** Debug: also measure with the DOM sentinel. */
  readonly legendMeasureSentinel: Option<LiveData.Readable<boolean>>;
  /** Debug: where the measurement is emitted. */
  readonly legendMeasureDebug: LiveData<Option<LegendMeasureComparison>>;
  /** Re-apply the legend's sticky top. */
  rebindLegendStickyTop(): void;
  /** The host's row-resize hook (`SheetElement.onTryResize`). */
  readonly onTryResize: Option<(row: SheetRow.Joined.Handle, n: number) => number>;
}

/** `SheetElement.legendHandle`: the legend column's resize and height rebind. */
export interface LegendHandle {
  readonly resizeDelegate: SheetLegend.ResizeDelegate;
  beginDragResize(): void;
  endDragResize(): void;
  rebindHeights(sheet: SheetViewModel.Surface<SheetBinder>): void;
}

export async function rebindLegend(host: LegendHostingHost, sheetChanged: boolean): Promise<number> {
  // A rebuilt legend MUST repaint: its per-row `$content` is destroyed and
  // re-made below with an EMPTY month/year label, and the only painter is
  // `onViewportColumnsChanged` — which is gated on `lastVisibleColumnsKey`.
  // The reset in `bind()` covers the outer entry, but `bindSheet` re-entries
  // (structural rebinds — e.g. weather data arriving right after a sheet
  // switch) and `rebindLegendMeasurement` reach here WITHOUT `bind()`: the
  // visible range is unchanged, every subsequent emit early-returns, and the
  // freshly emptied header legend stays blank for the life of the sheet
  // (2026-08-05 operator repro: month label + header-surface labels blank
  // the moment a sheet is clicked). Clearing at this choke point makes the
  // invariant hold for every path that rebuilds the legend.
  host.forgetVisibleColumns();
  const state = host.state;
  const element = host.core.element;
  const $legends = [
    host.legends.legend,
    host.legends.headerLegend,
    host.legends.footerLegend,
  ];

  if (sheetChanged) {
    element.toggleAttribute("legend-ready", false);
  }
  // Create/refresh pretext measurer from design system CSS variables.
  // No DOM probe needed — reads --cap-height, --baseline, font-family.
  // Cached for the element's lifetime: the measurer reads design-system CSS
  // vars (--cap-height, --baseline, font sizes) that are static per document
  // — and `fromStyleContext`'s getComputedStyle on a freshly-invalidated
  // 6k-node tree forces a full style recalc (~measured 100-200ms per call
  // at 4x throttle on a switch). Rebuilding it per sheet/VM change re-read
  // the same constants at the worst possible moment.
  if (!state.pretextMeasurer) {
    state.pretextMeasurer = PretextLegendMeasurer.fromStyleContext(element);
  }
  const measurer = state.pretextMeasurer;

  host.rebindLegendStickyTop();
  const promises: Promise<unknown>[] = [];
  for (const [idx, $legend] of $legends.entries()) {
    const isHeader = idx === 1;
    $legend.assignStyles({
      "--opacity": sheetChanged ? "0" : "1",
      "--std-calendar-legend-background-color":
        isHeader ? "var(--bg-elevated)" : "var(--bg)",
    });
  }
  for (const [idx, $legend] of $legends.entries()) {
    const isHeader = idx === 1;
    const bound = host.core.sheet;
    if (bound == null) {
      $legend.bindNullState();
      return 0;
    }
    $legend.bindNow(
      bound,
      host.handle,
      host.activeRowOptions,
      isHeader ? "header" : "other",
      host.resizeDelegate,
      promises,
    );
  }
  await Promise.all(promises);

  host.legendColumn.style.setProperty(
    "--legend-bottom-offset",
    `calc(${SheetElementLayout.bottomOffset()}px + var(--body-top-offset, 0px) + var(--footer-height, 0px))`,
  );

  // Measure width using constant-time pretext measurer (always available).
  const sheet = nonnull(host.core.sheet, "Sheet: no sheet bound");
  const layout = sheet.layout;
  const hasLayout = (row: SheetRow.Joined.Handle) =>
    layout.rowLayout.get(row.id) != null;
  const topLevelRows = sheet.joinedTree.viewChildren(null);
  const sheetTimescale = sheet.timescale;
  const pm = nonnull(host.pluginManager, "Sheet: pluginManager not set");

  let minWidthPx: number;
  if (host.legendMeasureToast?.get()) {
    const t0 = performance.now();
    const result = measurer.measureWithDebug(
      topLevelRows,
      sheetTimescale,
      pm,
      hasLayout,
    );
    minWidthPx = result.width;
    emitDebugMeasurement(host, 
      minWidthPx,
      performance.now() - t0,
      result.widestRow,
    );
  } else {
    minWidthPx = measurer.measure(
      topLevelRows,
      sheetTimescale,
      pm,
      hasLayout,
    );
  }

  // The measurement above still runs (its debug emission is the point of the
  // toast path); only the APPLIED width changes. An override is taken
  // verbatim — no 150px floor — and the inline `minWidth`/`width` it writes
  // beats the `#sheet-legend { min-width: 150px }` rule in $styles.
  //
  // Collapse is layered on top as a boolean, not as a third width "mode":
  // it borrows the rail and hands it straight back, so the expanded branch
  // below is untouched and every caller keeps the width policy it already
  // declared. Note the measurement is NOT short-circuited while collapsed —
  // it is constant-time (pretext, no DOM probe), it keeps the measure-debug
  // toast reporting the real widest row rather than 28px, and it means
  // expanding is a width write instead of a measure-then-write round trip.
  const collapsed = host.legendCollapsed;
  const override = host.legendWidthOverridePx;
  const appliedPx =
    collapsed ?
      (host.legendCollapsedWidthPx ?? host.defaultCollapsedLegendPx)
    : (override ?? Math.max(150, minWidthPx));

  return new Promise<number>((resolve) => {
    $$.mutate(() => {
      const widthPx = appliedPx + "px";
      for (const $legend of [
        host.legends.legend,
        host.legends.headerLegend,
        host.legends.footerLegend,
      ]) {
        $legend.assignStyles({
          minWidth: widthPx,
          width: widthPx,
          "--opacity": "1",
        });
      }
      // Same mutate as the width write, deliberately: the attribute is what
      // hides the rail's content, so splitting them would paint one frame of
      // full-width-but-empty (or 28px-wide-with-content spilling out). NOT
      // `legend-ready` — that attribute has exactly two owners (bindSheet's
      // atomic applyLayout mutate and the layout-gate onChange) and setting
      // it here reveals the legend a frame before body geometry lands.
      element.toggleAttribute("legend-collapsed", collapsed);
      // The condensed projection is an EMBED CHOICE, keyed off the same
      // signal that sizes the lane: an explicit legendCollapsedWidthPx. The
      // default 28px drawer (null) stays a blank strip — without this gate,
      // every DefaultRow's surviving name would render as a mid-glyph
      // fragment in a lane never sized to hold one.
      element.toggleAttribute(
        "legend-condensed",
        collapsed && host.legendCollapsedWidthPx != null,
      );
      host.leftOccludedWidthPx.update(appliedPx);
      resolve(appliedPx);
    });
  });
}

/** Debug: emit pretext measurement, optionally with sentinel comparison. */
function emitDebugMeasurement(
  host: LegendHostingHost,
  pretextWidth: number,
  pretextMs: number,
  widestRow: Option<string>,
): void {
  if (!host.legendMeasureSentinel?.get()) {
    host.legendMeasureDebug.update({
      pretextWidth,
      pretextMs,
      sentinelWidth: 0,
      sentinelMs: 0,
      diff: 0,
      widestRow,
    });
    return;
  }
  const state = host.state;
  if (!state.legendSentinel) {
    state.legendSentinel = LegendSentinel.make();
    nonnull(host.core.element.shadowRoot, "Sheet: shadowRoot not attached").appendChild(state.legendSentinel);
  }
  const sentinel = state.legendSentinel;
  const sheet = nonnull(host.core.sheet, "Sheet: no sheet bound");
  const handle = host.handle;
  const activeRowOptions = host.activeRowOptions;
  const resizeDelegate = host.resizeDelegate;
  requestIdleCallback(
    async () => {
      const t0 = performance.now();
      const sentinelWidth = await sentinel.measure(
        sheet,
        handle,
        activeRowOptions,
        resizeDelegate,
      );
      const sentinelMs = performance.now() - t0;
      const comparison = {
        pretextWidth,
        pretextMs,
        sentinelWidth,
        sentinelMs,
        diff: pretextWidth - sentinelWidth,
        widestRow,
      };
      Tracing.log("legend-measure/comparison", comparison);
      host.legendMeasureDebug.update(comparison);
    },
    { timeout: 500 },
  );
}

/**
 * The element's `legendHandle`: the legend's row-resize delegate, the
 * drag-resize bracket and the legend rows' height rebind. Moved out of
 * sheet-element.ts unchanged (r5 FS 2).
 */
export function makeLegendHandle(host: LegendHostingHost): LegendHandle {
  const state = host.state;
  const handle: LegendHandle = {
      get resizeDelegate(): SheetLegend.ResizeDelegate {
        return {
          onTryResize: (row, n) => host.onTryResize?.(row, n) ?? n,
          onBeginDragResize: () => handle.beginDragResize(),
          onEndDragResize: () => handle.endDragResize(),
        };
      },

      beginDragResize() {
        state.isDragResizing = true;
        if (state.dragResizeTimeout != null) clearTimeout(state.dragResizeTimeout);
        state.dragResizeTimeout = window.setTimeout(() => {
          if (state.isDragResizing) {
            state.isDragResizing = false;
            host.core.sheet?.invalidateLayout();
          }
        }, 10_000);
      },

      endDragResize() {
        state.isDragResizing = false;
        state.lastDragBodyMarginTop = null;
        if (state.dragResizeTimeout != null) {
          clearTimeout(state.dragResizeTimeout);
          state.dragResizeTimeout = null;
        }
        host.core.sheet?.invalidateLayout();
      },

      rebindHeights(sheet: SheetViewModel.Surface<SheetBinder>) {
        for (const $legend of [host.legends.legend, host.legends.headerLegend]) {
          for (const [rowId, $child] of $legend.$children) {
            const layout = sheet.layout.rowLayout.get(rowId);
            if (!layout) continue;
            $child.assignStyles({
              "--height": layout.cellHeightWithChildrenPx + "px",
            });
            for (const [childId, $nested] of $child.$children) {
              const childLayout = sheet.layout.rowLayout.get(childId);
              if (!childLayout) continue;
              $nested.assignStyles({
                "--height": childLayout.cellHeightWithChildrenPx + "px",
              });
            }
          }
        }
      },
  };
  return handle;
}
